import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldAlert, Phone, MapPin, AlertTriangle, CheckCircle2, Clock, Train, Users, Volume2, VolumeX, Navigation, RefreshCw, ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';
import { sirenPlayer } from './EmergencySiren';
import { toast } from '../../hooks/use-toast';

import { BACKEND_URL } from '../../config';
const API_BASE = BACKEND_URL;

export const SosTrackingPage = () => {
  const { alertId } = useParams();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sirenPlaying, setSirenPlaying] = useState(false);
  const [resolving, setResolving] = useState(false);

  const fetchAlert = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/sos/track/${alertId}`);
      setAlert(res.data.alert);
    } catch (err) {
      toast({ title: 'Alert not found or expired', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => {
    document.title = `🚨 SOS Tracking: ${alertId} – TrainPanda`;
    fetchAlert();

    // Auto play 15s emergency siren on tracking page load if alert is ACTIVE
    sirenPlayer.play(15);
    setSirenPlaying(true);

    // Auto refresh location every 15s
    const interval = setInterval(fetchAlert, 15000);

    return () => {
      clearInterval(interval);
      sirenPlayer.stop();
    };
  }, [alertId, fetchAlert]);

  const toggleSiren = () => {
    if (sirenPlaying) {
      sirenPlayer.stop();
      setSirenPlaying(false);
    } else {
      sirenPlayer.play(15);
      setSirenPlaying(true);
    }
  };

  const handleResolve = async () => {
    if (!window.confirm('Mark this emergency SOS alert as Resolved / Safe?')) return;
    setResolving(true);
    try {
      await axios.post(`${API_BASE}/api/sos/resolve/${alertId}`);
      toast({ title: 'Alert marked as Resolved' });
      sirenPlayer.stop();
      setSirenPlaying(false);
      fetchAlert();
    } catch (err) {
      toast({ title: 'Error resolving alert' });
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50 gap-3">
        <Loader2 className="w-10 h-10 text-red-600 animate-spin" />
        <p className="text-sm font-bold text-gray-700">Connecting to live SOS emergency feed...</p>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50 text-center space-y-4">
        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
          <AlertTriangle className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">SOS Session Not Found</h2>
        <p className="text-xs text-gray-500 max-w-sm">The emergency alert ID {alertId} may have expired or been resolved.</p>
        <Link to="/" className="px-5 py-2.5 bg-gray-900 text-white font-bold text-xs rounded-xl">
          Return to Home
        </Link>
      </div>
    );
  }

  const hasCoords = alert.latitude && alert.longitude;
  const isResolved = alert.status === 'RESOLVED';

  // Generate OpenStreetMap Embed URL
  const mapEmbedUrl = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${alert.longitude - 0.03}%2C${alert.latitude - 0.03}%2C${alert.longitude + 0.03}%2C${alert.latitude + 0.03}&layer=mapnik&marker=${alert.latitude}%2C${alert.longitude}`
    : null;

  const googleMapsUrl = hasCoords
    ? `https://www.google.com/maps/search/?api=1&query=${alert.latitude},${alert.longitude}`
    : null;

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Navigation & Alert Status Bar */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-600 hover:text-black bg-white px-3.5 py-2 rounded-xl shadow-sm border border-gray-200"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>

          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-sm ${
                isResolved
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                  : 'bg-red-600 text-white animate-pulse'
              }`}
            >
              {isResolved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
              {isResolved ? 'RESOLVED / SAFE' : 'ACTIVE EMERGENCY'}
            </span>

            <button
              onClick={toggleSiren}
              className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 shadow-sm transition-all ${
                sirenPlaying ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border border-gray-200'
              }`}
            >
              {sirenPlaying ? <><Volume2 className="w-3.5 h-3.5" /> Siren Playing</> : <><VolumeX className="w-3.5 h-3.5" /> Muted</>}
            </button>
          </div>
        </div>

        {/* Hero Emergency Banner */}
        <div className="rounded-3xl bg-gradient-to-r from-red-700 via-rose-700 to-red-800 text-white p-6 sm:p-8 shadow-xl shadow-red-500/15 space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-[11px] font-black uppercase tracking-widest text-yellow-300 bg-white/20 px-3 py-1 rounded-full">
                🚨 {alert.emergency_type || 'Emergency'}
              </span>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-2">
                {alert.passenger_name} is in an Emergency
              </h1>
              <p className="text-xs text-red-100 mt-1 flex items-center gap-2 font-mono">
                <Clock className="w-3.5 h-3.5" />
                Alert Triggered: {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>

            {/* Quick Action Dial Buttons */}
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {alert.user_mobile && (
                <a
                  href={`tel:${alert.user_mobile}`}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-white hover:bg-gray-100 text-red-700 font-bold text-xs rounded-xl shadow-md flex items-center justify-center gap-2"
                >
                  <Phone className="w-4 h-4 fill-red-700" /> Call Passenger
                </a>
              )}
            </div>
          </div>

          {/* Passenger Train / Coach Details Pill Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/15">
              <span className="text-[10px] uppercase font-bold text-red-200">Train</span>
              <p className="text-sm font-black">{alert.train_number || 'Running Train'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/15">
              <span className="text-[10px] uppercase font-bold text-red-200">Coach / Seat</span>
              <p className="text-sm font-black">{alert.coach || 'N/A'} {alert.berth ? `(Seat ${alert.berth})` : ''}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/15">
              <span className="text-[10px] uppercase font-bold text-red-200">Nearest Station</span>
              <p className="text-sm font-black">{alert.station || 'In Transit'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/15">
              <span className="text-[10px] uppercase font-bold text-red-200">GPS Accuracy</span>
              <p className="text-sm font-black">{alert.accuracy_meters ? `±${Math.round(alert.accuracy_meters)}m` : 'High Precision'}</p>
            </div>
          </div>
        </div>

        {/* Live Map Tracking View */}
        <div className="bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-md">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/75">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-600" />
              <h3 className="font-bold text-gray-900 text-sm">Live GPS Location Tracking</h3>
            </div>

            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Navigation className="w-3.5 h-3.5" /> Open in Google Maps
              </a>
            )}
          </div>

          {mapEmbedUrl ? (
            <div className="relative w-full h-80 sm:h-96 bg-gray-100">
              <iframe
                title="Passenger Live Location"
                src={mapEmbedUrl}
                className="w-full h-full border-0"
                loading="lazy"
              />
              <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-xl text-[11px] font-mono text-gray-800 border border-gray-200 shadow">
                Lat: {alert.latitude.toFixed(5)}, Lng: {alert.longitude.toFixed(5)}
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500 space-y-2">
              <MapPin className="w-8 h-8 text-gray-300 mx-auto" />
              <p className="text-sm font-semibold">GPS Location Coordinates Not Available for this Alert</p>
            </div>
          )}
        </div>

        {/* Incident Message & Notified Family Circle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Incident Message */}
          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm space-y-2">
            <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              Emergency Situation Details
            </h4>
            <p className="text-xs text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100 leading-relaxed font-medium">
              "{alert.message || 'Immediate assistance requested.'}"
            </p>
          </div>

          {/* Notified Family Members */}
          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm space-y-3">
            <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              Family Circle Notified ({alert.family_members?.length || 0})
            </h4>

            <div className="space-y-2 max-h-36 overflow-y-auto">
              {alert.family_members?.map((m) => (
                <div key={m.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <span className="font-bold text-gray-900">{m.name}</span>
                    <span className="text-gray-500 ml-1.5 font-medium">({m.relationship})</span>
                  </div>
                  <a
                    href={`tel:${m.contact_number}`}
                    className="text-emerald-700 font-bold flex items-center gap-1 hover:underline"
                  >
                    <Phone className="w-3 h-3" /> Call
                  </a>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Resolve Button */}
        {!isResolved && (
          <div className="pt-2 text-center">
            <button
              onClick={handleResolve}
              disabled={resolving}
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/20 inline-flex items-center gap-2 transition-all"
            >
              <CheckCircle2 className="w-4 h-4" />
              {resolving ? 'Updating Status...' : 'Mark Passenger as Safe / Resolved'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};
export default SosTrackingPage;
